import { makeStyles } from '@material-ui/core';
import { useState, MouseEvent, TouchEvent, useEffect, useRef, useCallback } from 'react';

type PanZoomPictureProps = {
  src?: string;
  alt?: string;
}

export const PanZoomPicture = ({ src, alt }: PanZoomPictureProps) => {
  const [mouseDown, setMouseDown] = useState(false);
  const [pinchStart, setPinchStart] = useState(false);
  const { root, paper, image } = useStyles();
  const paperRef = useRef<HTMLDivElement>(null);

  const position = useRef([0, 0]);
  const onMove = useCallback((x: number, y: number) => {
    const delta = [mousePos.current[0] - x, mousePos.current[1] - y];
    position.current = [
      position.current[0] - delta[0],
      position.current[1] - delta[1],
    ];
    if(paperRef.current) {
      paperRef.current.style.left = position.current[0] + 'px';
      paperRef.current.style.top = position.current[1] + 'px';
    }
    mousePos.current = [x, y];
  }, []);

  const zoom = useRef(1);
  const onZoom = useCallback((newZoom: number) => {
    if (newZoom < 1) {
      zoom.current = 1;
    }
    else if (newZoom > 8) {
      zoom.current = 8;
    } else {
      zoom.current = newZoom; 
    }

    if(paperRef.current) {
      paperRef.current.style.transform = `scale(${zoom.current})`;
    }

  }, []);

  const repan = () => {
    const width = paperRef.current.clientWidth;
    const height = paperRef.current.clientHeight;
    // 1 = 0
    // 2 = ((zoom - 1) / 2) * width = width / 2
    // 3 = width
    // 4 = width + 1/2 * width
    const widthSpace = ((zoom.current - 1) / 2) * width;
    const heightSpace = ((zoom.current - 1) / 2) * height;
    
    if(position.current[0] > widthSpace) {
      position.current[0] = widthSpace;
    } 
    if(position.current[0] < -widthSpace) {
      position.current[0] = -widthSpace;
    } 
    if(position.current[1] > heightSpace) {
      position.current[1] = heightSpace;
    }
    if(position.current[1] < -heightSpace) {
      position.current[1] = -heightSpace;
    }
    if(paperRef.current) {
      paperRef.current.style.left = position.current[0] + 'px';
      paperRef.current.style.top = position.current[1] + 'px';
    }
  };

  useEffect(() => {
    let repanTimer: NodeJS.Timeout;
    const handleScroll = (e: WheelEvent) => {
      const delta = 0.25 * (e.deltaY / 8);
      onZoom(zoom.current + delta);

      if(repanTimer) {
        clearTimeout(repanTimer);
      }
      repanTimer = setTimeout(repan, 300);
    };
    window.addEventListener('wheel', handleScroll);

    return () => {
      window.removeEventListener('wheel', handleScroll);
    };
  },[]);
  
  const mousePos = useRef<[number, number]>([0,0]);
  const hypot = useRef(0);
  const oldZoom = useRef(0);
  
  const onMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    setMouseDown(true);
    mousePos.current = [e.clientX, e.clientY];
  };

  const onMouseUp = () => {
    setMouseDown(false);
    setTimeout(repan, 200);
  };

  const onMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!mouseDown) {return;}
    onMove(e.clientX, e.clientY);
  };

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
    mousePos.current = [e.touches[0].clientX, e.touches[0].clientY];
    if(e.touches.length > 1) {
      setPinchStart(true);
      hypot.current = Math.hypot(
        e.touches[0].pageX - e.touches[1].pageX,
        e.touches[0].pageY - e.touches[1].pageY);
      oldZoom.current = zoom.current;
      return;
    }
    setMouseDown(true);
  };

  const onTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if(e.touches.length > 1) {
      const dist = Math.hypot(
        e.touches[0].pageX - e.touches[1].pageX,
        e.touches[0].pageY - e.touches[1].pageY);

      const delta = hypot.current / dist;
      hypot.current = dist;
      const newZoom = zoom.current / delta;
      
      onZoom(newZoom);
      return;
    }
    onMove(e.touches[0].clientX, e.touches[0].clientY);
  };

  return <div className={root} 
    onMouseDown={onMouseDown} 
    onMouseUp={onMouseUp}
    onMouseLeave={onMouseUp}
    onMouseMove={onMouseMove}
    onTouchStart={onTouchStart}
    onTouchEnd={onMouseUp}
    onTouchMove={onTouchMove}
  >
    <div className={paper} ref={paperRef} style={{
      transition: mouseDown ? 'none' : 'all 0.1s linear',
    }}
    >
      <div className={image} style={{
        backgroundImage: 'url(' + src + ')',
      }}
      />
    </div>
  </div>;
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    flex: '1',
    minHeight: '80vh',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      margin: '0 8px 0 8px',
      width: 'calc(100% - 16px)',
      minHeight: 'calc(100vh - 230px)',
    },
  },
  paper: {
    height: '100%',
    width: '100%',
    cursor: 'pointer',
    position: 'absolute',
  },
  image: {
    backgroundPosition: 'center',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    height: '100%',
    width: '100%',
  }
}));